import React, { useState } from 'react';
import { Box, IconButton, List, ListItem, Typography } from '@material-ui/core';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@material-ui/icons';
import ConfirmDialog from 'dialogs/ConfirmDialog';
import { IRealEstateReport } from '../interfaces';
import { usePublishOrDraftRealEstateReport, useReplaceRealEstateReportFile } from '../hooks';
import RealEstateReport from './RealEstateReport';
import ReplaceReportFileDialog from './ReplaceReportFileDialog';

interface IProps {
  list: IRealEstateReport[];
  totalCount: number;
  pageNo: number;
  pageSize: number;
  onChangePage: (page: number) => void;
  onRefresh?: () => void;
}

const RealEstateReports: React.FC<IProps> = ({ list, totalCount, pageNo, pageSize, onChangePage, onRefresh }) => {
  const [currentReport, setCurrentReport] = useState<IRealEstateReport | null>(null);
  const [confirmPublishReportDialogOpened, setOpenedConfirmPublishReportDialog] = useState(false);
  const [replaceReportFileDialogOpened, setOpenedReplaceReportFileDialog] = useState(false);

  const { publishOrDraftReport, loading: publishOrDraftReportLoading } = usePublishOrDraftRealEstateReport(() => {
    setOpenedConfirmPublishReportDialog(false);
    onRefresh?.();
  });
  const { replaceFile, loading: replaceFileLoading } = useReplaceRealEstateReportFile(onRefresh);

  function handleOpenConfirmPublishReportDialog(report: IRealEstateReport) {
    setCurrentReport(report);
    setOpenedConfirmPublishReportDialog(true);
  }

  function handleOpenReplaceReportFileDialog(report: IRealEstateReport) {
    setCurrentReport(report);
    setOpenedReplaceReportFileDialog(true);
  }

  return (
    <>
      <List>
        {list.map((report) => (
          <ListItem disableGutters key={report.realEstateReportId}>
            <RealEstateReport
              report={report}
              disabled={publishOrDraftReportLoading || replaceFileLoading}
              onDraft={(report) => publishOrDraftReport(report.realEstateReportId, true)}
              onPublish={handleOpenConfirmPublishReportDialog}
              onReplaceFile={handleOpenReplaceReportFileDialog}
            />
          </ListItem>
        ))}

        <ListItem disableGutters>
          <Box sx={{ py: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
            <IconButton aria-label="Назад" disabled={pageNo === 1} onClick={() => onChangePage(pageNo - 1)}>
              <KeyboardArrowLeft />
            </IconButton>

            <IconButton
              aria-label="Вперёд"
              disabled={pageNo === Math.ceil(totalCount / pageSize)}
              onClick={() => onChangePage(pageNo + 1)}
            >
              <KeyboardArrowRight />
            </IconButton>
          </Box>
        </ListItem>
      </List>

      <ConfirmDialog
        open={confirmPublishReportDialogOpened}
        title="Вы уверены что хотите опубликовать отчет?"
        disabled={publishOrDraftReportLoading}
        onConfirm={() => publishOrDraftReport(currentReport!.realEstateReportId, false)}
        onClose={() => setOpenedConfirmPublishReportDialog(false)}
      >
        <Typography variant="body2" color="textSecondary">
          После публикации отчет будет доступен для просмотра и скачивания. Вы сможете его отредактировать,
          предварительно скачав и сняв публикацию
        </Typography>
      </ConfirmDialog>

      <ReplaceReportFileDialog
        open={replaceReportFileDialogOpened}
        disabled={replaceFileLoading}
        onClose={() => setOpenedReplaceReportFileDialog(false)}
        onSubmitFile={(file) => replaceFile(currentReport!.realEstateReportId, file)}
      />
    </>
  );
};

export default RealEstateReports;
